import { useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { Chip } from '@/components/commons/chip'
import {
	EmptyImagePlaceholder,
	GalleryImageItem
} from '@/components/commons/gallery-image-item'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { AlertTriangleIcon, Expand01Icon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip } from '@/components/ui/tooltip'
import { useUpdateExperienceItineraryStatus } from '@/features/experience-vetting/queries/use-update-experience-itinerary-status.ts'
import type { RemappedItinerariesVetting } from '@/features/experience-vetting/types.ts'
import {
	OpsVettingCardBodyContainer,
	OpsVettingCardContainer,
	OpsVettingCardTitle,
	OpsVettingLabel,
	OpsVettingParagraph
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { VettingSectionStatusSelector } from '@/features/ops-vetting/components/vetting-section-status-selector'
import { useExperienceVettingError } from '@/features/service-vetting/store/use-service-vetting-error'
import { raise } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n.tsx'
import { useAddExperienceItineraryAnswerFlags } from '../queries/use-add-experience-itinerary-answer-flags'
import { useRemoveExperienceItineraryAnswerFlags } from '../queries/use-remove-experience-itinerary-answer-flags'
import { useRestoreExperienceVettingCriteriaItineraryCoveryImage } from '../queries/use-restore-experience-vetting-criteria-itinerary-covery-image'
import { useUpdateExperienceVettingCriteriaItineraryCoveryImage } from '../queries/use-update-experience-vetting-criteria-itinerary-covery-image'
import { ExpVettingCriteriaChoosePhotoDialog } from './exp-vetting-criteria-choose-photo-dialog'
import { ExperienceVettingCriteriaFlags } from './experience-vetting-criteria-flags'

export const ExperienceVettingCriteriaItineraryCard = ({
	itinerary,
	index,
	galleryId,
	isReadOnly
}: {
	itinerary: RemappedItinerariesVetting
	index: number
	galleryId: number
	isReadOnly: boolean
}) => {
	const { t } = useTranslation()
	const { mutate: updateItineraryStatus } =
		useUpdateExperienceItineraryStatus({
			experienceId: itinerary.submissionId,
			itineraryId: itinerary.id
		})
	const errors = useExperienceVettingError((state) => state.errors)
	const isEditedImage =
		itinerary.originalCoverImageId !== null &&
		itinerary.originalCoverImageId !== itinerary.coverImageId

	const addItineraryQuestionFlags = useAddExperienceItineraryAnswerFlags({
		experienceId: itinerary.submissionId,
		sectionId: itinerary.id
	})
	const removeItineraryQuestionFlags =
		useRemoveExperienceItineraryAnswerFlags({
			experienceId: itinerary.submissionId,
			sectionId: itinerary.id
		})

	const { mutate: updateCoveryImage, isPending: isUpdatingCoveryImage } =
		useUpdateExperienceVettingCriteriaItineraryCoveryImage({
			experienceId: itinerary.submissionId,
			itineraryId: itinerary.id
		})
	const { mutate: restoreCoveryImage, isPending: isRestoringCoveryImage } =
		useRestoreExperienceVettingCriteriaItineraryCoveryImage({
			experienceId: itinerary.submissionId,
			itineraryId: itinerary.id
		})

	const isAccepted = itinerary.status === 'accepted'
	const isBackToDraft = itinerary.status === 'back_to_draft'

	const [showGalleryModal, setShowGalleryModal] = useState(false)

	const handleClickExpandImage = () => {
		setShowGalleryModal(true)
	}

	useHotkeys('Esc', () => setShowGalleryModal(false), {
		enabled: showGalleryModal
	})

	return (
		<OpsVettingCardContainer direction='column' gap={2}>
			<div className='flex w-full items-center justify-between gap-2'>
				<div className='flex items-center gap-8'>
					<OpsVettingCardTitle>
						{t('vetting:criteria.experience.itinerary.activity_n', {
							count: index + 1
						})}
					</OpsVettingCardTitle>
					<Tooltip
						content={
							<div className='flex flex-col gap-1'>
								<h4 className='font-medium'>
									{t(
										'vetting:criteria.experience.itinerary.edited_photo_tooltip.title'
									)}
								</h4>{' '}
								<p className='text-neutral-300 text-xs'>
									{t(
										'vetting:criteria.experience.itinerary.edited_photo_tooltip.description'
									)}
								</p>
							</div>
						}
						maxWidth={'214px'}
						rootProps={{ open: isEditedImage ? undefined : false }}
						triggerProps={{ asChild: true }}
					>
						<div>
							<VettingSectionStatusSelector
								disabled={isReadOnly || isEditedImage}
								hideDeclined
								onChange={(status) =>
									updateItineraryStatus({ status })
								}
								value={itinerary.status}
							/>
						</div>
					</Tooltip>
				</div>
				{errors.itemErrors
					.filter((error) => error.itemId === itinerary.id)
					.map((error) => (
						<Chip
							isActive
							key={`${error.itemId}-${error.error}`}
							label={t(error.error)}
							startSlot={<AlertTriangleIcon />}
							variant={'danger'}
						/>
					))}
			</div>
			<OpsVettingCardBodyContainer>
				<div className='flex w-full flex-col gap-2.5'>
					<div className='flex w-full flex-col gap-2'>
						<OpsVettingLabel>
							{t(
								'vetting:criteria.experience.itinerary.question_title'
							)}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{itinerary.answers.title?.answer}
						</OpsVettingParagraph>
						{isBackToDraft && (
							<ExperienceVettingCriteriaFlags
								addFlag={(flag) =>
									addItineraryQuestionFlags.mutate({
										flags: [flag],
										itemId: itinerary.answers.title.id
									})
								}
								flags={itinerary.answers.title.flags}
								hideAction={isReadOnly}
								removeFlag={(flag) =>
									removeItineraryQuestionFlags.mutate({
										flags: [flag],
										itemId: itinerary.answers.title.id
									})
								}
								scopes={['itinerary_title']}
							/>
						)}
					</div>
					<div className='flex w-full flex-col gap-2'>
						<OpsVettingLabel>
							{t(
								'vetting:criteria.experience.itinerary.question_description'
							)}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{itinerary.answers.description.answer}
						</OpsVettingParagraph>
						{isBackToDraft && (
							<ExperienceVettingCriteriaFlags
								addFlag={(flag) =>
									addItineraryQuestionFlags.mutate({
										flags: [flag],
										itemId: itinerary.answers.description.id
									})
								}
								flags={itinerary.answers.description.flags}
								hideAction={isReadOnly}
								removeFlag={(flag) =>
									removeItineraryQuestionFlags.mutate({
										flags: [flag],
										itemId: itinerary.answers.description.id
									})
								}
								scopes={['itinerary_description']}
							/>
						)}
					</div>
					<div className='flex w-full flex-col gap-2'>
						<OpsVettingLabel>
							{t(
								'vetting:criteria.experience.itinerary.question_duration'
							)}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{itinerary.answers.duration?.answer &&
								t(
									'vetting:criteria.experience.itinerary.x_minutes',
									{
										count: itinerary.answers.duration
											?.answer
									}
								)}
						</OpsVettingParagraph>
						{/* {isBackToDraft && itinerary.answers.duration && (
                            <ExperienceVettingCriteriaFlags
                                flags={itinerary.answers.duration?.flags || []}
                                addFlag={flag =>
                                    addItineraryQuestionFlags.mutate({
                                        flags: [flag],
                                        itemId: itinerary.answers.duration?.id || raise('Duration ID is required')
                                    })
                                }
                                removeFlag={flag =>
                                    removeItineraryQuestionFlags.mutate({
                                        flags: [flag],
                                        itemId: itinerary.answers.duration?.id || raise('Duration ID is required')
                                    })
                                }
                                hideAction={isReadOnly}
                            />
                        )}
                    */}{' '}
					</div>
				</div>
				<div className='flex w-full flex-col gap-2.5'>
					{itinerary.coverImage ? (
						<GalleryImageItem
							bottomRightSlot={
								!isReadOnly && (
									<ButtonLegacy
										className='show-on-hover'
										onClick={handleClickExpandImage}
										shape='square'
										size='sm'
										type='button'
										variant='secondary'
									>
										<Expand01Icon />
									</ButtonLegacy>
								)
							}
							src={
								itinerary.coverImage?.thumbnails?.xs ||
								itinerary.coverImage?.url
							}
						/>
					) : (
						<EmptyImagePlaceholder />
					)}
					{isBackToDraft && itinerary.coverImage && (
						<ExperienceVettingCriteriaFlags
							addFlag={(flag) =>
								addItineraryQuestionFlags.mutate({
									flags: [flag],
									itemId:
										itinerary.answers.main_photo_url?.id ||
										raise('Cover image ID is required')
								})
							}
							flags={
								itinerary.answers.main_photo_url?.flags || []
							}
							hideAction={isReadOnly}
							removeFlag={(flag) =>
								removeItineraryQuestionFlags.mutate({
									flags: [flag],
									itemId:
										itinerary.answers.main_photo_url?.id ||
										raise('Cover image ID is required')
								})
							}
							scopes={['photo_gallery']}
						/>
					)}
					{isAccepted && !isReadOnly && (
						<div className='flex w-full justify-between'>
							<ExpVettingCriteriaChoosePhotoDialog
								defaultSelectedImageId={
									itinerary.coverImageId || undefined
								}
								experienceId={itinerary.submissionId}
								galleryId={galleryId}
								onConfirm={(coverImageId) =>
									updateCoveryImage({ coverImageId })
								}
								trigger={
									<ButtonLegacy
										disabled={
											isUpdatingCoveryImage || isReadOnly
										}
										type='button'
										variant={'tertiary'}
									>
										{t('common:edit')}{' '}
										{isUpdatingCoveryImage && <Spinner />}
									</ButtonLegacy>
								}
							/>
							{isEditedImage && !isReadOnly && (
								<ButtonLegacy
									disabled={isRestoringCoveryImage}
									onClick={() => restoreCoveryImage()}
									type='button'
									variant={'tertiary'}
								>
									{t(
										'vetting:criteria.service.offerings.restore_photo'
									)}
									{isRestoringCoveryImage && <Spinner />}
								</ButtonLegacy>
							)}
						</div>
					)}
				</div>
			</OpsVettingCardBodyContainer>
			{showGalleryModal && itinerary.coverImage && (
				<GalleryModal
					currentImage={itinerary.coverImage}
					headerSlot={
						<GalleryModalHeader
							currentImage={itinerary.coverImage}
							onClose={() => setShowGalleryModal(false)}
						/>
					} //TODO: add real thumbnails
					onClose={() => setShowGalleryModal(false)}
					thumbnails={{
						l: itinerary.coverImage?.url || '',
						m: '',
						s: '',
						xs: ''
					}}
				/>
			)}
		</OpsVettingCardContainer>
	)
}
